import { useEffect, useState } from "react";
import {
  ChevronRight,
  Download,
  FileArchive,
  FileText,
  FileUp,
  LoaderCircle,
  Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type DocumentTemplate = {
  id: string | null;
  templateKey: string;
  module: string;
  documentType: string;
  name: string;
  description: string | null;
  builderKey: string;
  mergeSchemaVersion: number;
  templateFileKey: string | null;
  status: "draft" | "published" | "archived" | "missing";
  isActive: boolean;
  version: number;
  createdAt: string | null;
  updatedAt: string | null;
  isMissing: boolean;
};

export function DokumentmalerPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void bootstrap();
  }, []);

  async function bootstrap() {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/api/admin/document-templates/templates`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Kunne ikke hente dokumentmaler.");
      }

      const templateData = (await response.json()) as DocumentTemplate[];
      setTemplates(templateData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke hente dokumentmaler.");
    } finally {
      setLoading(false);
    }
  }

  async function uploadTemplate(template: DocumentTemplate, file: File) {
    if (!template.id) {
      return;
    }

    setUploadingId(template.id);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/api/admin/document-templates/templates/${template.id}/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Kunne ikke laste opp .docx-fil.");
      }

      await bootstrap();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke laste opp .docx-fil.");
    } finally {
      setUploadingId(null);
    }
  }

  async function downloadTemplate(template: DocumentTemplate) {
    if (!template.id) {
      return;
    }

    setDownloadingId(template.id);
    setError(null);

    try {
      const response = await fetch(`${API}/api/admin/document-templates/templates/${template.id}/download-url`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Kunne ikke hente nedlastingslenke.");
      }

      const data = (await response.json()) as { downloadUrl: string };
      window.open(data.downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke hente nedlastingslenke.");
    } finally {
      setDownloadingId(null);
    }
  }

  function openUploadPicker(template: DocumentTemplate) {
    if (!template.id) {
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        void uploadTemplate(template, file);
      }
    };
    input.click();
  }

  const filteredTemplates = templates.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.templateKey.toLowerCase().includes(q) ||
      t.module.toLowerCase().includes(q)
    );
  });

  const stats = [
    { label: "Systemmaler", value: templates.length, icon: FileArchive, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Med .docx", value: templates.filter(t => !!t.templateFileKey).length, icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Manglende", value: templates.filter(t => t.isMissing).length, icon: FileText, color: "text-rose-600", bg: "bg-rose-50" },
  ];

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            Dokumentmaler
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Systemdokumenter bundet til bestemt forretningslogikk.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive">
          {error}
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {stats.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
                <p className="mt-1 text-2xl font-black tracking-tight text-foreground">
                  {loading ? "..." : value.toLocaleString("nb-NO")}
                </p>
              </div>
              <div className={`flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0 ${bg}`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Søk etter malnavn, nøkkel eller modul..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Table — desktop */}
      <Card className="hidden sm:block overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mal</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Info</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fil</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">Laster maler...</td></tr>
            ) : filteredTemplates.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">Ingen maler funnet</td></tr>
            ) : (
              filteredTemplates.map((t) => (
                <tr
                  key={t.templateKey}
                  className="hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/dev/dokumentmaler/${t.templateKey}`)}
                >
                  <td className="px-6 py-4">
                    <p className="font-medium text-foreground">{t.name}</p>
                    <p className="text-[10px] font-mono text-muted-foreground/60">{t.templateKey}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t.module} / {t.documentType}</p>
                    <p className="text-xs text-muted-foreground/80 line-clamp-1">{t.description}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <Badge variant={t.isMissing ? "destructive" : t.status === "published" ? "success" : "secondary"} className="w-fit">
                        {t.status}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); openUploadPicker(t); }}
                        disabled={!t.id || uploadingId === t.id}
                      >
                        {uploadingId === t.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); void downloadTemplate(t); }}
                        disabled={!t.templateFileKey || downloadingId === t.id}
                      >
                        {downloadingId === t.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      </Button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center text-sm text-muted-foreground py-8">Laster maler...</p>
        ) : filteredTemplates.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">Ingen maler funnet</p>
        ) : (
          filteredTemplates.map((t) => (
            <Card key={t.templateKey} className="p-4 space-y-3" onClick={() => navigate(`/dev/dokumentmaler/${t.templateKey}`)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-foreground">{t.name}</p>
                  <p className="text-[10px] font-mono text-muted-foreground/60">{t.templateKey}</p>
                </div>
                <Badge variant={t.isMissing ? "destructive" : t.status === "published" ? "success" : "secondary"}>
                  {t.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  onClick={(e) => { e.stopPropagation(); openUploadPicker(t); }}
                  disabled={!t.id || uploadingId === t.id}
                >
                  {uploadingId === t.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <FileUp className="h-3 w-3" />}
                  Oppdater .docx
                </Button>
                {t.templateFileKey && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1.5"
                    onClick={(e) => { e.stopPropagation(); void downloadTemplate(t); }}
                    disabled={downloadingId === t.id}
                  >
                    {downloadingId === t.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                    Last ned
                  </Button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
